import { z } from "zod";

/** M11 round one, the front door: the shapes the welcome step, the creation form and the invitation list share with the API. */

/** The key one mailbox is counted by (the sign-up limits; brief "The words", "Address variants"): trimmed, lower case, the `+tag` removed
 *  on every domain, the dots before the @ removed on Gmail only (other providers treat a dot as a real character, so two people at a company
 *  that writes `a.berger` and `ab.erger` stay two). The domain is kept. */
export function emailKey(address: string): string {
  const a = address.trim().toLowerCase();
  const at = a.lastIndexOf("@");
  if (at < 0) return a;
  const domain = a.slice(at + 1);
  let local = a.slice(0, at).replace(/\+.*$/, "");
  if (GMAIL_DOMAINS.has(domain)) local = local.replace(/\./g, "");
  return `${local}@${domain}`;
}
const GMAIL_DOMAINS = new Set(["gmail.com", "googlemail.com"]);

/** The address check's answers (GET /api/v1/workspaces/address?slug=): free, or why not, in words. */
export const WORKSPACE_ADDRESS_STATES = ["free", "taken", "reserved", "retired", "shape"] as const;
export const WorkspaceAddressSchema = z.object({ status: z.enum(WORKSPACE_ADDRESS_STATES), slug: z.string(), message: z.string() });
export type WorkspaceAddress = z.infer<typeof WorkspaceAddressSchema>;

/** POST /api/v1/workspaces: the creation form. `termsVersion` is the version in force the form showed; `termsAccepted` the box. */
export const WorkspaceCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  slug: z.string().trim().min(1).max(60),
  termsVersion: z.string().max(40).optional(),
  termsAccepted: z.boolean().optional(),
}).strict();
export type WorkspaceCreate = z.infer<typeof WorkspaceCreateSchema>;

/** One invitation waiting for the signed-in address (GET /api/v1/me/invitations): who invited the person where, and whether it ran out. */
export const MyInvitationSchema = z.object({
  id: z.string(),
  workspace: z.object({ id: z.string(), slug: z.string(), name: z.string(), mark: z.string() }),
  inviter: z.object({ name: z.string() }),
  role: z.string(),
  expiresAt: z.string(),
  expired: z.boolean(),
});
export type MyInvitation = z.infer<typeof MyInvitationSchema>;
/** Accept answers the workspace the person joined */
export const JoinedSchema = z.object({ id: z.string(), slug: z.string(), name: z.string() });
export type Joined = z.infer<typeof JoinedSchema>;

/** The sentence an expired invitation answers with: whom to ask, by name */
export const expiredInvitationSentence = (workspace: string, inviter: string) => `Your invitation to ${workspace} expired. Ask ${inviter} for a new one`;

/* ---------- M11 round two: the owner's setup checklist and the sample ticket ---------- */

/** The checklist's five steps in the prototype's order (brief choice 6); `sending` only on a plan with own domains (question 3); `own_ai`
 *  ("Connect your AI and work your queue", "Work my queue" round two) only on a plan with connected AIs */
export const SETUP_STEP_IDS = ["mail", "docs", "team", "own_ai", "sending"] as const;
export type SetupStepId = (typeof SETUP_STEP_IDS)[number];
/** What the steps are ticked from, read from the workspace's real state (never from a click on the row) */
export type SetupState = {
  /** a project whose forwarding setup is confirmed */ mailConfirmed: boolean;
  /** a non-sample article, or a files or website source (the sample article, a repository and the resolved-tickets source do not count) */ docs: boolean;
  /** a second active member or an open invitation */ team: boolean;
  /** a member's own AI wrote a draft or a note in the workspace (its audit rows: kept when the sample goes) */ ownAi: boolean;
  /** a project sending from a verified own domain */ ownSending: boolean;
  /** the plan allows own domains */ ownDomains: boolean;
  /** the plan allows connected AIs (the `mcp` feature) */ mcp: boolean;
};
export const SetupStepSchema = z.object({ id: z.enum(SETUP_STEP_IDS), done: z.boolean() });
/** Home's `setup` (the owner's alone; null for everyone else): the steps, the owner's dismissal, the sample ticket's number while it exists,
 *  the first project (the steps' deep links) and the owner's switch "Members may connect their own AI" (the AI step leads to it while off) */
export const HomeSetupSchema = z.object({ steps: z.array(SetupStepSchema), dismissed: z.boolean(), sample: z.number().int().nullable(), projectId: z.string().nullable(), aiConnections: z.boolean() });
export type HomeSetup = z.infer<typeof HomeSetupSchema>;
/** PUT /api/v1/w/:slug/home/setup: the owner hides the card, or brings it back (Undo) */
export const HomeSetupDismissSchema = z.object({ dismissed: z.boolean() }).strict();

/** The steps from the state, in order; without own domains on the plan the sending step is left out, without connected AIs the AI step */
export function setupSteps(s: SetupState): { id: SetupStepId; done: boolean }[] {
  const all: { id: SetupStepId; done: boolean }[] = [
    { id: "mail", done: s.mailConfirmed }, { id: "docs", done: s.docs }, { id: "team", done: s.team },
    { id: "own_ai", done: s.ownAi }, { id: "sending", done: s.ownSending },
  ];
  return all.filter((x) => (x.id !== "sending" || s.ownDomains) && (x.id !== "own_ai" || s.mcp));
}
export const setupComplete = (steps: { done: boolean }[]): boolean => steps.every((s) => s.done);
/** The audit actions of a member's own AI that tick "Connect your AI and work your queue": a draft, a note, a finish with a note (a finish
 *  is audited only when it leaves one) */
export const SETUP_OWN_AI_ACTIONS = ["connection.write_draft", "connection.add_note", "connection.finish_task"] as const;

/** The sample ticket every workspace made through the creation form starts with (brief choice 7): made-up, never mailed, outside every
 *  count, report and AI run; deleted by the owner, with the checklist complete, or 30 days after it was made */
export const SAMPLE = {
  customer: { name: "Anna Berger", email: "anna.berger@example.com" },
  subject: "Can I get a refund on my annual plan?",
  message: "Hi,\n\nI signed up for the annual plan three weeks ago, but we have decided to move our support to a shared mailbox for now. Is it possible to get a refund for the rest of the year? If not, can I at least cancel so it does not renew?\n\nThanks,\nAnna",
  article: { title: "Refunds and cancellations (sample)", body: "Annual plans are refunded in full within 30 days of purchase. After that a plan can be cancelled at any time and does not renew; the time already paid stays usable." },
  draft: "Hi Anna,\n\nThanks for letting us know, and no problem at all. Annual plans can be refunded in full within 30 days of purchase, so you are still inside the window: I have started the refund, and it reaches your card within five to ten working days.\n\nYour account stays open until the refund is through, and you can come back whenever you like.\n\nBest regards",
  summary: "Wants a refund on an annual plan bought three weeks ago, or at least no renewal. Inside the 30-day window.",
} as const;
/** how long a sample lives before the daily job deletes it */
export const SAMPLE_DAYS = 30;
/** The refusals' words */
export const SAMPLE_NEVER_MAILED = "This is the sample ticket: nothing on it is ever mailed, and it gets no AI work";
export const SAMPLE_OWNER_ONLY = "Only the owner removes the sample";
